import { useState, useEffect } from "react";
import { useUpdateAssetsMutation } from "@/hooks/assets";
import { useAuth } from "@/hooks/auth";
import { usePostCurrentUploadCommentMutation } from "@/hooks/comments";
import {
  useCurrentUploadQuery,
  useUploadUpdateMutation,
} from "@/hooks/uploads";
import Text from "@ingka/text";
import { useLocation, useNavigate } from "react-router";
import { Loading } from "../Loading/Loading";
import { useMetadataQuery } from "@/hooks/metadata";

import {
  UpdateAssetDtoStateEnum,
  UpdateUploadDtoStateEnum,
} from "generated/api";
import { NavigationButtons } from "../NavigationButtons/NavigationButtons";
import ContactPerson from "./components/optional/ContactPerson";
import { OtherNegotiatedUsageRights } from "./components/optional/OtherNegotiatedUsageRights";
import Language from "./components/optional/Language";
import Tags from "./components/optional/Tags";
import DisclaimerText from "./components/optional/AssetDisclaimer";
import Description from "./components/optional/Description";
import AdditionalInformationText from "./components/optional/AdditionalInformationText";
import DeleteUpload from "../DeleteUpload/DeleteUpload";
import { useToast } from "@/hooks/toast";
import { useStore } from "@/services/store";
import { AssetsChangeMap } from "@/services/store/slices/mandatoryMetaData/types";
import { useShallow } from "zustand/react/shallow";
import {
  FieldAccessContext,
  isDocumentUpload,
  isLockedForOwner,
  isUploadFinalized,
} from "./formFieldsAccessRules";
import { toRouteError } from "@/errors/routeError";

const OptionalMetadata = () => {
  const navigate = useNavigate();
  const { isAdmin, isIngkaUser } = useAuth().user;
  const commentMutation = usePostCurrentUploadCommentMutation();
  const uploadUpdateMutation = useUploadUpdateMutation();
  const updateAssetsMutation = useUpdateAssetsMutation();
  const { status, comment } = useStore(
    useShallow((state) => ({ status: state.status, comment: state.comment })),
  );
  const { pathname } = useLocation();
  const currentUploadQuery = useCurrentUploadQuery();
  const metadataQuery = useMetadataQuery();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (
      commentMutation.isPending ||
      uploadUpdateMutation.isPending ||
      updateAssetsMutation.isPending
    ) {
      setIsUpdating(true);
    } else {
      setIsUpdating(false);
    }
  }, [
    commentMutation.isPending,
    uploadUpdateMutation.isPending,
    updateAssetsMutation.isPending,
  ]);

  if (
    status === "ERROR" ||
    metadataQuery.isError ||
    currentUploadQuery.isError
  ) {
    throw toRouteError(metadataQuery.error ?? currentUploadQuery.error ?? status, {
      scope: "metadata",
      uploadId: currentUploadQuery.data?.id,
    });
  }

  if (
    status === "LOADING" ||
    !metadataQuery.isSuccess ||
    !currentUploadQuery.isSuccess
  ) {
    return <Loading text="Loading asset metadata" />;
  }

  const upload = currentUploadQuery.data!;
  const { state: uploadState, type: uploadType } = upload;
  const uploadId = upload.id;
  const isAdminEdit = pathname.startsWith("/admin/");
  const fieldAccessContext: FieldAccessContext = {
    isAdmin,
    isIngkaUser,
    uploadState,
    uploadType,
  };
  const isDisabled =
    isLockedForOwner(fieldAccessContext) ||
    isUploadFinalized(fieldAccessContext);
  const isSubmitDisabled = isDisabled || isUpdating;

  if (
    commentMutation.isError ||
    uploadUpdateMutation.isError ||
    updateAssetsMutation.isError
  ) {
    throw new Error(
      "Error occurred while transitioning the upload to the next state",
    );
  }

  const handleSubmissionError = () => {
    setIsSubmitting(false);
    toast(
      "Something went wrong. Please try again or contact Ingka DAM support slack channel if the error persists.",
    );
  };

  const updateAssetStates = async (newState: UpdateAssetDtoStateEnum) => {
    const assets: AssetsChangeMap =
      upload.assets.reduce((acc: AssetsChangeMap, asset) => {
        acc[asset.id] = { state: newState };
        return acc;
      }, {} as AssetsChangeMap) || {};
    const result = await updateAssetsMutation.mutateAsync(assets);
    return !result.some((item) => item.status === "rejected");
  };

  const handleUploadState = async (newState: UpdateUploadDtoStateEnum) => {
    const message = comment?.trim();
    const createComment =
      message &&
      commentMutation.mutateAsync({ createCommentDto: { uploadId, message } });
    const updateUpload = uploadUpdateMutation.mutateAsync({
      id: uploadId,
      updateUploadDto: {
        state: newState,
      },
    });
    const [createCommentResponse, updateUploadResponse] =
      await Promise.allSettled([createComment, updateUpload]);
    return (
      (!createComment || createCommentResponse.status === "fulfilled") &&
      updateUploadResponse.status === "fulfilled"
    );
  };

  const adminApprove = async () => {
    if (isAdminEdit) {
      navigate(`/admin/${uploadId}`);
    } else {
      setIsSubmitting(true);
      if (
        (await updateAssetStates("DONE")) &&
        (await handleUploadState("DONE"))
      ) {
        navigate(`/overview`);
      } else {
        handleSubmissionError();
      }
    }
  };

  const sendForReview = async () => {
    setIsSubmitting(true);
    if (await handleUploadState("SUBMITTED")) {
      navigate(`/uploads/${uploadId}/under-review`);
    } else {
      handleSubmissionError();
    }
  };

  const getButtonText = () => {
    if (isSubmitting) {
      return "Submitting...";
    }
    if (isAdminEdit) {
      return "Done editing";
    }
    return "Submit";
  };

  const { bynder } = metadataQuery.data;
  return (
    <div className="flex flex-col">
      <section className="flex-1">
        <Text tagName="h3" headingSize="xs" className="mb-space300">
          Optional metadata
        </Text>
        <div className="grid grid-cols-2 gap-x-space150 gap-y-space75">
          <div className="col-start-1 row-start-1">
            <ContactPerson disabled={isDisabled} />
          </div>
          <div className="col-start-2 row-start-1">
            <Language
              options={bynder.Language}
              disabled={isDisabled || isDocumentUpload(fieldAccessContext)}
            />
          </div>
          <div className="col-span-2 col-start-1 row-start-2">
            <OtherNegotiatedUsageRights disabled={isDisabled} />
          </div>
          <div className="col-span-2 col-start-1 row-start-3">
            <Tags disabled={isDisabled} />
          </div>
          <div className="col-start-1 row-start-4">
            <DisclaimerText disabled={isDisabled} />
          </div>
          <div className="col-start-2 row-start-4">
            {isAdmin ? (
              <Description disabled={isDisabled} />
            ) : (
              <AdditionalInformationText disabled={isDisabled} />
            )}
          </div>
        </div>
      </section>

      <div className="mb-space150 mt-space300 flex flex-row justify-end">
        {upload.state === "DRAFT" && (
          <DeleteUpload
            upload={upload}
            buttonText="Delete Draft"
            navigate={() => navigate("/overview")}
          />
        )}
        <NavigationButtons
          nextButtonTitle={getButtonText()}
          onPrev={() => {
            if (isAdminEdit) {
              navigate(`/admin/${uploadId}/edit`);
            } else {
              navigate(`/uploads/${uploadId}/mandatory-metadata`);
            }
          }}
          onContinue={isAdmin ? adminApprove : sendForReview}
          disabledPrev={isUpdating}
          disabledNext={isSubmitDisabled}
        />
      </div>
    </div>
  );
};

export default OptionalMetadata;
